import 'temporal-polyfill/global';

export type IsoDate = string; // "2030-01-01"
export type IsoUtcDate = string; // "2030-01-01"
export type IsoDateTime = string; // "2030-01-01T02:30:00+01:00"
export type IsoUtcDateTime = string; // "2030-01-01T01:30:00Z"
export type IsoTime = string; // "02:30:00"

export const isoDateRegexString = '^([1-3][0-9]{3})-(1[0-2]|0[1-9])-(3[01]|[12][0-9]|0[1-9])$';
export const isoUtcDateRegexString = isoDateRegexString;
export const isoDateTimeRegexString =
  '^([1-3][0-9]{3})-(1[0-2]|0[1-9])-(3[01]|[12][0-9]|0[1-9])T([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9][+-](0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$';
export const isoUtcDateTimeRegexString =
  '^([1-3][0-9]{3})-(1[0-2]|0[1-9])-(3[01]|[12][0-9]|0[1-9])T([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]Z$';
export const isoTimeRegexString = '^([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$';

/**
 * Checks if a string is a valid ISO date string.
 * @param input The string to check. ("2030-01-01")
 * @returns True if the string is a valid ISO date string, false otherwise.
 */
export function isIsoDate(input: string): input is IsoDate {
  const dateRegex = new RegExp(isoDateRegexString);
  return dateRegex.test(input);
}

/**
 * Checks if a string is a valid ISO date and time string.
 * @param input The string to check. ("2030-01-01T02:30:00Z")
 * @returns True if the string is a valid ISO date and time string, false otherwise.
 */
export function isIsoDateTime(input: string): input is IsoDateTime {
  const isoDateTimeRegex = new RegExp(isoDateTimeRegexString);
  return isoDateTimeRegex.test(input);
}

/**
 * Checks if a string is a valid ISO UTC date and time string.
 * @param input The string to check. ("2030-01-01T02:30:00+01:00")
 * @returns True if the string is a valid ISO UTC date and time string, false otherwise.
 */
export function isIsoUtcDateTime(input: string): input is IsoUtcDateTime {
  const isoUtcDateTimeRegex = new RegExp(isoUtcDateTimeRegexString);
  return isoUtcDateTimeRegex.test(input);
}

/**
 * Checks if a string is a valid ISO time string.
 * @param input The string to check. ("00:30:00")
 * @returns True if the string is a valid ISO time string, false otherwise.
 */
export function isIsoTime(input: string): input is IsoTime {
  const timeRegex = new RegExp(isoTimeRegexString);
  return timeRegex.test(input);
}

/**
 * Converts an ISO time string to a Temporal.Duration object.
 * @param time The ISO time string. ("00:30:00")
 * @returns The Temporal.Duration object.
 */
export function isoTimeToDuration(time: IsoTime): Temporal.Duration {
  if (!isIsoTime(time)) {
    return Temporal.Duration.from({ seconds: 0 });
  }
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return Temporal.Duration.from({ hours, minutes, seconds });
}

/**
 * Adds a duration to a UTC ISO date and time.
 * @param startAtUtc The start date and time in UTC ISO format. ("2030-01-01T01:30:00Z")
 * @param duration The duration to add. ("00:30:00")
 * @returns The end date and time in UTC ISO format. ("2030-01-01T02:00:00Z")
 */
export function addIsoTimeToIsoUtcDateTime(
  startAtUtc: IsoUtcDateTime,
  duration: IsoTime,
): IsoUtcDateTime {
  const instant = Temporal.Instant.from(startAtUtc);
  const durationObj = isoTimeToDuration(duration);
  const endInstant = instant.add(durationObj);
  return stripMilliseconds(endInstant.toString()) as IsoUtcDateTime;
}

export function addIsoTimeToIsoDateTime(startAtLocal: IsoDateTime, duration: IsoTime): IsoDateTime {
  const instant = Temporal.Instant.from(startAtLocal);
  const durationObj = isoTimeToDuration(duration);
  const endInstant = instant.add(durationObj);
  return stripMilliseconds(endInstant.toString()) as IsoDateTime;
}

/**
 * Returns the current date in IsoDate format.
 * @returns The current date in IsoDate format (e.g., "2030-01-01")
 */
export function currentIsoDate(): IsoDate {
  return Temporal.Now.plainDateISO().toString() as IsoDate;
}

/**
 * Returns the current date and time in IsoUtcDateTime format with an optional offset.
 * @param offset The offset to add to the current date and time. Defaults to 0.
 * @param units The units to add to the current date and time. Defaults to 'days'. Possible values are 'days', 'hours', 'minutes', 'seconds', and 'milliseconds'.
 * @returns The current date and time in UTC ISO format (e.g., "2030-01-01T12:34:10Z")
 */
export function currentIsoUtcDateTime(
  offset?: number,
  units: 'days' | 'hours' | 'minutes' | 'seconds' | 'milliseconds' = 'days',
): IsoUtcDateTime {
  if (!['days', 'hours', 'minutes', 'seconds', 'milliseconds'].includes(units)) {
    throw new Error(`Invalid unit: ${units}`);
  }
  if (!offset) {
    return Temporal.Now.zonedDateTimeISO('UTC').toString() as IsoUtcDateTime;
  }
  const now = Temporal.Now.zonedDateTimeISO('UTC');
  const offsetDate = now.add({ [units]: offset });
  return stripMilliseconds(offsetDate.toString()) as IsoUtcDateTime;
}

/**
 * Converts an ISO date string from its time zone to UTC.
 * @param date The ISO date string to convert. ("2030-01-01T02:30:00+01:00")
 * @returns The same date in the UTC time zone. ("2030-01-01T01:30:00Z")
 */
export function isoDateTimeToIsoUtcDateTime(date: IsoDateTime): IsoUtcDateTime | undefined {
  try {
    const instant = Temporal.Instant.from(date);
    return stripMilliseconds(instant.toString()) as IsoUtcDateTime;
  } catch {
    return undefined;
  }
}

export function stripMilliseconds(date: IsoDateTime): IsoDateTime {
  return date.replace(/\.\d{3}Z$/, 'Z') as IsoDateTime;
}

export function stripSeconds(date: IsoDateTime): IsoDateTime {
  return date.replace(/:\d{2}\.\d{3}Z$/, 'Z') as IsoDateTime;
}

/**
 * Converts an ISO date string from its time zone to UTC and returns the date only.
 * @param date The ISO date string to convert. ("2030-01-01T02:30:00+01:00")
 * @returns The same date in the UTC time zone. ("2030-01-01" or undefined if invalid)
 */
export function isoDateTimeToIsoUtcDate(date: IsoDateTime): IsoUtcDate | undefined {
  try {
    const instant = Temporal.Instant.from(date);
    const utcDateTime = instant.toZonedDateTimeISO('UTC');
    return utcDateTime.toPlainDate().toString() as IsoUtcDate;
  } catch {
    return undefined;
  }
}

/**
 * Converts a UTC ISO date string to a timezone-specific ISO date string.
 * @param date The UTC ISO date string to convert. ("2030-01-01T01:30:00Z")
 * @param timezoneName The IANA timezone name. ("America/New_York")
 * @returns The date in the specified timezone. ("2030-01-01T02:30:00-05:00" or undefined if invalid)
 */
export function isoUtcDateTimeToTimezone(
  date: IsoUtcDateTime,
  timezoneName: string,
): IsoDateTime | undefined {
  try {
    const instant = Temporal.Instant.from(date);
    const zonedDateTime = instant.toZonedDateTimeISO(timezoneName);
    return zonedDateTime.toString({
      timeZoneName: 'never',
    }) as IsoDateTime;
  } catch {
    return undefined;
  }
}

/**
 * Forces the offset of a UTC date to a specific ISO offset.
 * @param date The UTC date string. ("2030-01-01T00:00:00Z")
 * @param isoOffset The ISO offset string. ("-05:00") Defaults to the current local offset.
 * @returns The modified ISO date string with the forced offset. ("2030-01-01T00:00:00-05:00")
 */
export function utcDateForceOffset(date: IsoDateTime, isoOffset?: string): IsoDateTime {
  if (!isoOffset) {
    const now = Temporal.Now.zonedDateTimeISO();
    const offset = now.offset;
    isoOffset = `${offset.startsWith('+') ? '' : '+'}${offset.slice(0, 2)}:${offset.slice(2)}`;
  }
  const iso = date.replace('Z', isoOffset);
  return iso;
}

/**
 * Converts a JavaScript Date object to a UTC ISO string with no milliseconds.
 * @param date The JavaScript Date object to convert. ("Sat Mar 02 2030 10:30:00 GMT-0500 EST")
 * @returns The UTC ISO string representation of the input date. ("2030-03-02T15:30:00Z")
 */
export function jsDateToIsoUtcDateTime(date: Date): IsoUtcDateTime {
  const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
  return stripMilliseconds(instant.toString()) as IsoUtcDateTime;
}

/**
 * Converts a JavaScript Date object to a UTC ISO string with the date only.
 * @param date The JavaScript Date object to convert. ("Sat Mar 02 2030 10:30:00 GMT-0500 EST")
 * @returns The UTC ISO string representation of the input date. ("2030-03-02")
 */
export function jsDateToIsoUtcDate(date: Date): IsoUtcDate {
  const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
  const utcDateTime = instant.toZonedDateTimeISO('UTC');
  return utcDateTime.toPlainDate().toString() as IsoUtcDate;
}

export function ageFromDateOfBirth(dateOfBirth: IsoDate): number | undefined {
  if (!dateOfBirth) return undefined;

  try {
    const today = Temporal.Now.plainDateISO();
    const birthDate = Temporal.PlainDate.from(dateOfBirth);
    const age = today.since(birthDate, {
      largestUnit: 'years',
      roundingMode: 'floor',
    }).years;
    return age;
  } catch {
    return undefined;
  }
}

/**
 * Returns tomorrow's date in IsoDate format.
 * @returns Tomorrow's date in IsoDate format (e.g., "2030-01-02")
 */
export function getTomorrowIsoDate(): IsoDate {
  const today = Temporal.Now.plainDateISO();
  const tomorrow = today.add({ days: 1 });
  return tomorrow.toString() as IsoDate;
}

/**
 * Returns tomorrow's date in IsoDate format.
 * @returns Tomorrow's date in IsoDate format (e.g., "2030-01-02")
 */
export function getTodayPlainDate(): Temporal.PlainDate {
  return Temporal.Now.plainDateISO();
}

export function getNowMilliseconds(): number {
  return Temporal.Now.instant().epochMilliseconds;
}

export function getTimeSince(
  dateTime: IsoDateTime,
  unit: 'hours' | 'minutes' | 'seconds' | 'milliseconds',
): number {
  const instant = Temporal.Instant.from(dateTime);
  const now = Temporal.Now.instant();
  const unitsSince = now.since(instant)[unit];
  return unitsSince;
}

/**
 * Parses an RFC 822 date string (e.g., from RSS feeds) to a JavaScript Date object.
 * @param dateString The RFC 822 date string to parse (e.g., "Mon, 02 Jun 2025 00:00:00 GMT")
 * @returns The parsed Date object, or undefined if invalid or empty
 */
export function parseRfc822DateTime(dateString: string | undefined): Date | undefined {
  if (!dateString) return undefined;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return undefined;
    return date;
  } catch {
    return undefined;
  }
}
